import React, { useState } from 'react';
import type { Campaign, Account } from '../../store/appStore';
import { useAppStore } from '../../store/appStore';
import { DEMOKRATIUKA_COLORS, getCampaignTheme } from '../../lib/demokratiukaBrand';

interface Props {
  campaign: Campaign;
  account: Account;
  isQR: boolean;
  location?: string | null;
}

export const PosterResponseForm: React.FC<Props> = ({ campaign, account, isQR, location }) => {
  const { addSuggestion } = useAppStore();
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await addSuggestion(account.id, message.trim(), name.trim() || null, isQR ? 'qr' : 'direct', campaign.id, location || null);
      setMessage('');
      setName('');
      setShowSuccess(true);
    } catch {
      setError('Kunne ikke sende. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="text-center py-12">
        <div className="p-4 sm:p-6 mb-6" style={{ backgroundColor: '#ffffff', border: '2px solid #000000' }}>
          <h3 style={{ color: '#000000', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Takk!</h3>
          <p style={{ color: '#222222', fontSize: '1rem', fontWeight: 400 }}>Svaret ditt er registrert.</p>
        </div>
        <button
          onClick={() => setShowSuccess(false)}
          className="text-white transition-colors"
          style={{ backgroundColor: '#000000', fontSize: '0.875rem', fontWeight: 600, padding: '0.75rem 2rem' }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = '#485cf0')}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = '#000000')}
        >
          Send nytt svar
        </button>
      </div>
    );
  }

  const theme = getCampaignTheme(campaign.slug);
  const mutedInk = theme.ink === '#ffffff' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.65)';

  return (
    <div>
      {/* Provocative statement — themed per kampanje */}
      <div className="mb-6 sm:mb-8 px-4 sm:px-6 py-6 sm:py-8" style={{ backgroundColor: theme.bg }}>
        <p
          className="uppercase mb-3"
          style={{ color: mutedInk, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' }}
        >
          Hva mener du?
        </p>
        <h1
          className="leading-tight"
          style={{ color: theme.ink, fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          {campaign.title}
        </h1>
        {campaign.description && (
          <p className="mt-3" style={{ color: mutedInk, fontSize: '1rem', lineHeight: 1.7, fontWeight: 300 }}>
            {campaign.description}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div style={{ backgroundColor: '#ffffff', border: '2px solid #ef6f64', padding: '0.75rem 1rem' }}>
            <p style={{ color: '#ef6f64', fontSize: '0.875rem', fontWeight: 500 }}>{error}</p>
          </div>
        )}

        <div>
          <label style={{ color: '#000000', fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Ditt svar
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Skriv hva du mener..."
            required
            disabled={isSubmitting}
            rows={4}
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1rem',
              fontFamily: 'inherit',
              border: '2px solid #000000',
              borderRadius: 0,
              backgroundColor: '#ffffff',
              color: '#000000',
              resize: 'none',
              outline: 'none',
              lineHeight: 1.7,
            }}
            onFocus={e => (e.currentTarget.style.borderColor = '#485cf0')}
            onBlur={e => (e.currentTarget.style.borderColor = '#000000')}
          />
        </div>

        <div>
          <label style={{ color: '#000000', fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
            Navn (valgfritt)
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ditt navn"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1rem',
              fontFamily: 'inherit',
              border: '2px solid #000000',
              borderRadius: 0,
              backgroundColor: '#ffffff',
              color: '#000000',
              outline: 'none',
            }}
            onFocus={e => (e.currentTarget.style.borderColor = '#485cf0')}
            onBlur={e => (e.currentTarget.style.borderColor = '#000000')}
          />
          <p style={{ color: '#888888', fontSize: '0.75rem', marginTop: '0.375rem' }}>
            Unngå etternavn og personopplysninger. Fornavn er valgfritt.
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !message.trim()}
          className="w-full text-white transition-colors"
          style={{
            backgroundColor: '#000000',
            fontSize: '0.875rem',
            fontWeight: 600,
            padding: '0.75rem 1.5rem',
            opacity: isSubmitting || !message.trim() ? 0.4 : 1,
          }}
          onMouseOver={e => { if (!isSubmitting && message.trim()) e.currentTarget.style.backgroundColor = '#485cf0'; }}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = '#000000')}
        >
          {isSubmitting ? 'Sender...' : 'Send svar'}
        </button>
        <p style={{ textAlign: 'center', color: '#999999', fontSize: '0.65rem', marginTop: '10px' }}>
          Ved å sende inn godtar du vår <a href="/personvern" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>personvernerklæring</a>
        </p>
      </form>
    </div>
  );
};
